<template>
  <div class="fixed top-0 left-0 h-screen w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col border-r border-slate-700 shadow-2xl z-10">
    <!-- Header with logo and language switcher -->
    <div class="h-20 flex items-center justify-between px-4 border-b border-slate-700">
      <div class="flex items-center">
        <!-- Логотип вместо текста -->
        <div class="flex items-center space-x-3">
          <div class="text-white">
            <div class="font-bold text-lg tracking-wide">EMDEU</div>
          </div>
        </div>
      </div>
      <div class="ml-2">
        <LanguageSwitcher />
      </div>
    </div>
    
    <!-- Navigation menu -->
    <nav class="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
      
      <router-link 
        v-for="item in menuItems" 
        :key="item.name"
        :to="item.path"
        class="group flex items-center px-3 py-3 text-base font-medium rounded-lg transition-all duration-200 ease-in-out relative"
        :class="isActive(item.path) ? 
          'bg-slate-800 text-white border-l-3 border-blue-500' : 
          'text-slate-300 hover:text-white hover:bg-slate-800/50'"
      >
        <span class="font-medium">{{ item.label }}</span>
      </router-link>
    </nav>
    
    <!-- Documents section -->
    <div class="px-4 py-2 border-t border-slate-700">
      <h3 class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
        {{ $t('navigation.documents') }}
      </h3>
      <div class="space-y-1">
        <router-link 
          to="/privacy-policy"
          class="group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ease-in-out text-slate-400 hover:text-white hover:bg-slate-800/50"
          :class="isActive('/privacy-policy') ? 'bg-slate-800 text-white border-l-3 border-blue-500' : ''"
        >
          <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
          </svg>
          <span>{{ $t('documents.privacyPolicy') }}</span>
        </router-link>
        
        <router-link 
          to="/user-agreement"
          class="group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ease-in-out text-slate-400 hover:text-white hover:bg-slate-800/50"
          :class="isActive('/user-agreement') ? 'bg-slate-800 text-white border-l-3 border-blue-500' : ''"
        >
          <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <span>{{ $t('documents.userAgreement') }}</span>
        </router-link>
      </div>
    </div>
    
    <!-- User info section - moved to bottom -->
    <div class="px-4 py-4 border-t border-slate-700">
      <div v-if="userInfo" class="flex items-center space-x-3 mb-4">
        <div class="relative">
          <!-- Profile photo if available -->
          <div v-if="userInfo.user && userInfo.user.face_photo" 
               class="w-10 h-10 rounded-full overflow-hidden shadow-lg">
            <img :src="userInfo.user.face_photo" 
                 :alt="formatDoctorName()" 
                 class="w-full h-full object-cover"
                 @error="onImageError">
          </div>
          <!-- Default avatar if no photo -->
          <div v-else class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center shadow-lg">
            <svg class="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 border-2 border-slate-900 rounded-full"></div>
        </div>
        <div class="flex-1 min-w-0">
          <p class="font-semibold text-white text-xs truncate">{{ formatDoctorName() }}</p>
          <p class="text-slate-400 text-xs truncate">{{ formatUserMedicalInfo() }}</p>
          <div v-if="userInfo.city || userInfo.clinic" class="mt-0.5">
            <p class="text-slate-500 text-xs truncate flex items-center">
              <svg class="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              {{ formatLocation() }}
            </p>
          </div>
        </div>
      </div>
      <div v-else class="flex items-center space-x-3 mb-4">
        <HeartbeatLoader />
        <div class="flex-1">
          <div class="h-3 bg-slate-700 rounded mb-1"></div>
          <div class="h-2 bg-slate-700 rounded w-3/4"></div>
        </div>
      </div>
      
      <!-- Logout button -->
      <button 
        @click="logout"
        class="w-full flex items-center px-3 py-2 text-sm font-medium text-slate-300 rounded-lg hover:text-white hover:bg-red-600/10 hover:border-red-500/20 transition-all duration-200 group border border-transparent"
      >
        <svg class="w-4 h-4 mr-3 text-slate-400 group-hover:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
        </svg>
        <span>{{ $t('common.logout') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { useRouter } from 'vue-router'
import api from '../services/api'
import organizationsApi from '../services/organizationsApi'
import LanguageSwitcher from './LanguageSwitcher.vue'
import HeartbeatLoader from './common/HeartbeatLoader.vue'
import { formatDoctorSpecialization } from '../utils/doctorDisplay'

export default {
  name: 'DoctorSidebar',
  components: {
    LanguageSwitcher,
    HeartbeatLoader
  },
  data() {
    return {
      userInfo: null,
      userType: null,
      staffRole: null,
      menuItems: [],
      doctorOrganizations: []
    }
  },
  setup() {
    const router = useRouter()
    return { router }
  },
  async created() {
    await this.checkUserTypeAndFetchInfo()
    this.setMenuItems()
  },
  computed: {
    doctorInfo() {
      // Для обратной совместимости с шаблоном
      return this.userInfo
    },
    // Доступ к психомониторингу только для тестового врача Сембинова Алтынай
    canAccessPsychoMonitoring() {
      if (!this.userInfo || !this.userInfo.user) return false
      const user = this.userInfo.user
      const fullName = `${user.last_name || ''} ${user.first_name || ''}`.trim()
      return fullName.includes('Сембинова') && fullName.includes('Алтынай')
    },
    // Врач принадлежит к организациям
    hasOrganizations() {
      return this.doctorOrganizations && this.doctorOrganizations.length > 0
    }
  },
  watch: {
    '$i18n.locale'() {
      // Обновляем пункты меню при смене языка
      this.setMenuItems()
    },
    doctorOrganizations() {
      // Перестраиваем меню когда загружаются организации
      this.setMenuItems()
    }
  },
  methods: {
    async checkUserTypeAndFetchInfo() {
      try {
        // Сначала проверяем тип пользователя
        const authResponse = await api.checkAuth()
        const authUser = authResponse.data.user || {}
        this.userType = authUser.type
        this.staffRole = authUser.staff_role || null
        
        // Загружаем профиль в зависимости от типа пользователя
        if (this.userType === 'doctor') {
        const response = await api.getDoctorProfile()
          this.userInfo = response.data
          // Загружаем организации врача
          await this.loadDoctorOrganizations()
        } else if (this.userType === 'nurse') {
          const response = await api.getNurseProfile()
          this.userInfo = response.data
        } else if (this.userType === 'staff' && this.staffRole === 'clinic_manager') {
          let managerProfile = authUser
          try {
            const meResponse = await api.get('/users/me/')
            if (meResponse.data) {
              managerProfile = {
                ...authUser,
                ...meResponse.data,
                type: authUser.type,
                staff_role: this.staffRole
              }
            }
          } catch (error) {
            managerProfile = authUser
          }

          const assignedClinic = authUser.assigned_clinic
          let clinicData = null

          if (assignedClinic && typeof assignedClinic === 'object') {
            clinicData = assignedClinic
          } else if (assignedClinic) {
            try {
              const clinicResponse = await api.get(`/clinics/${assignedClinic}/`)
              clinicData = clinicResponse.data
            } catch (error) {
              clinicData = null
            }
          }

          this.userInfo = {
            user: managerProfile,
            clinic: clinicData,
            specialization: 'Менеджер клиники'
          }
        }
      } catch (error) {
      // Error handled silently
      }
    },
    async loadDoctorOrganizations() {
      try {
        const response = await organizationsApi.getDoctorOrganizations()
        this.doctorOrganizations = response.results || response || []
      } catch (error) {
        console.error('Error loading doctor organizations:', error)
        this.doctorOrganizations = []
      }
    },
    setMenuItems() {
      if (this.userType === 'staff' && this.staffRole === 'clinic_manager') {
        this.menuItems = [
          { name: 'clinic-appointments', path: '/clinic-appointments', label: 'Приемы поликлиники' },
          { name: 'clinic-schedule', path: '/clinic-schedule', label: 'Расписание врачей' }
        ]
        return
      }

      if (this.userType === 'doctor') {
        const hasClinic = !!(this.userInfo && this.userInfo.clinic)
        const items = [
          { name: 'home', path: '/', label: this.$t('navigation.home') },
          { name: 'calendar', path: '/doctor-schedule', label: this.$t('navigation.schedule') }
        ]

        // Для врача без клиники скрываем только пациентов клиники
        if (hasClinic) {
          items.push({ name: 'patients', path: '/patients', label: this.$t('navigation.patients') })
        }

        // Пациенты организации и лица ELSYS
        if (this.hasOrganizations) {
          items.push({
            name: 'organization-patients',
            path: '/organization-patients',
            label: this.$t('navigation.organizationPatients') || 'Пациенты организации'
          })
          items.push({
            name: 'detected-faces',
            path: '/detected-faces',
            label: this.$t('navigation.detectedFaces') || 'Зафиксированные лица'
          })
        }

        items.push({ name: 'appointments', path: '/appointments', label: this.$t('navigation.appointments') })
        items.push({ name: 'support', path: '/support', label: this.$t('navigation.support') })
        items.push({ name: 'profile', path: '/profile', label: this.$t('navigation.profile') })
        items.push({ name: 'certificate', path: '/certificate', label: this.$t('navigation.certificate') })

        // Психомониторинг только для тестового врача Сембинова Алтынай
        if (this.canAccessPsychoMonitoring) {
          items.push({
            name: 'psycho-monitoring',
            path: '/psychological-monitoring',
            label: this.$t('staffSidebar.psychoMonitoring') || 'Психомониторинг'
          })
        }

        this.menuItems = items
        return
      }

      // Базовые пункты для медсестры
      const baseItems = [
        { name: 'home', path: '/', label: this.$t('navigation.home') },
        { name: 'patients', path: '/patients', label: this.$t('navigation.patients') },
        { name: 'profile', path: '/profile', label: this.$t('navigation.profile') },
        { name: 'certificate', path: '/certificate', label: this.$t('navigation.certificate') }
      ]

      if (this.userType === 'nurse') {
        baseItems.splice(2, 0, { name: 'calls-history', path: '/nurse-calls-history', label: this.$t('navigation.callsHistory') })
      }

      this.menuItems = baseItems
    },
    formatDoctorName() {
      if (!this.userInfo || !this.userInfo.user) return ''
      
      const user = this.userInfo.user
      const parts = []
      
      if (user.last_name) parts.push(user.last_name)
      if (user.first_name) parts.push(user.first_name)
      if (user.middle_name) parts.push(user.middle_name)

      const hasEncryptedParts = parts.some(
        (part) => typeof part === 'string' && part.startsWith('ENC::')
      )
      if (hasEncryptedParts) {
        return this.userType === 'staff' && this.staffRole === 'clinic_manager'
          ? 'Менеджер клиники'
          : ''
      }
      
      if (this.userType === 'staff' && this.staffRole === 'clinic_manager') {
        return parts.join(' ') || 'Менеджер клиники'
      }

      return parts.join(' ') || (this.userType === 'nurse' ? this.$t('navigation.medical') : this.$t('doctor.doctors'))
    },
    formatUserMedicalInfo() {
      if (!this.userInfo) {
        return this.userType === 'nurse' ? this.$t('navigation.medical') : this.$t('doctor.doctors')
      }

      if (this.userType === 'staff' && this.staffRole === 'clinic_manager') {
        return 'Менеджер клиники'
      }

      if (this.userType === 'doctor') {
        return formatDoctorSpecialization(this.userInfo) || this.$t('doctor.doctors')
      }

      return this.userInfo.specialization || this.$t('navigation.medical')
    },
    formatLocation() {
      if (!this.userInfo) return ''
      const parts = []
      if (this.userInfo.city?.name) parts.push(this.userInfo.city.name)
      if (this.userInfo.clinic) parts.push(this.getLocalizedClinicName(this.userInfo.clinic))

      return parts.join(', ')
    },
    getLocalizedClinicName(clinic) {
      if (this.$i18n.locale === 'kk' && clinic.name_kz) {
        return clinic.name_kz;
      }
      // Возвращаем русское название или обычное name как fallback
      return clinic.name || '';
    },
    isActive(path) {
      return this.$route.path === path
    },
    async logout() {
      try {
        await api.logout()
        this.router.push('/login')
      } catch (error) {
        // Even if logout fails, redirect to login
        this.router.push('/login')
      }
    },
    onImageError(event) {
      // Скрываем изображение если оно не загрузилось
      event.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
/* Добавляем кастомную прокрутку */
nav::-webkit-scrollbar {
  width: 4px;
}

nav::-webkit-scrollbar-track {
  background: transparent;
}

nav::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.3);
  border-radius: 4px;
}

nav::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.5);
}

/* Минималистичное активное состояние */
.border-l-3 {
  border-left-width: 3px;
}
</style>
